import { OwnTelegramLinkSchema, SessionStateSchema, TelegramLinksSchema, type SessionState, type TelegramLink } from "@vekha/contracts";
import { z } from "zod";

import { ApiRequestError } from "./api";

const ErrorBody = z.object({ error: z.object({ code: z.string().optional(), message: z.string(), requestId: z.string().optional() }) });

async function call<T>(schema: { parse: (value: unknown) => T } | null, path: string,
  init: { method?: "GET" | "POST" | "DELETE"; body?: unknown; csrf?: string } = {}): Promise<T> {
  const method = init.method ?? (init.body === undefined ? "GET" : "POST");
  let response: Response;
  try {
    response = await fetch(path, {
      credentials: "include", method, signal: AbortSignal.timeout(20_000),
      headers: method === "GET" ? { Accept: "application/json" }
        : { Accept: "application/json", "Content-Type": "application/json", ...(init.csrf ? { "x-csrf-token": init.csrf } : {}) },
      ...(method === "GET" ? {} : { body: JSON.stringify(init.body ?? {}) }),
    });
  } catch {
    throw new ApiRequestError("Нет связи с Вехой.", { code: "REQUEST_FAILED" });
  }
  let value: unknown = null;
  try { value = await response.json(); } catch { value = null; }
  if (!response.ok) {
    const error = ErrorBody.safeParse(value);
    throw new ApiRequestError(error.success ? error.data.error.message : "Сервер не подтвердил действие.", {
      status: response.status,
      ...(error.success && error.data.error.code ? { code: error.data.error.code } : {}),
      ...(error.success && error.data.error.requestId ? { requestId: error.data.error.requestId } : {}),
    });
  }
  return schema ? schema.parse(value) : (undefined as T);
}

/** Sign-in by Telegram's launch data; TELEGRAM_NOT_LINKED — the id is not linked yet. */
export const signInWithTelegram = (initData: string): Promise<SessionState> =>
  call(SessionStateSchema, "/api/session/telegram", { body: { initData } });
export const linkTelegram = (initData: string, csrf: string): Promise<TelegramLink | null> =>
  call(OwnTelegramLinkSchema, "/api/session/telegram/link", { body: { initData }, csrf }).then((answer) => answer.link);
export const fetchOwnTelegramLink = (): Promise<TelegramLink | null> =>
  call(OwnTelegramLinkSchema, "/api/session/telegram/link").then((answer) => answer.link);
export const unlinkOwnTelegram = (csrf: string): Promise<void> =>
  call<void>(null, "/api/session/telegram/link", { method: "DELETE", csrf });
export const fetchTelegramLinks = (): Promise<TelegramLink[]> =>
  call(TelegramLinksSchema, "/api/access/telegram-links").then((answer) => answer.links);
export const unlinkTelegramOf = (userId: string, csrf: string): Promise<void> =>
  call<void>(null, `/api/access/telegram-links/${encodeURIComponent(userId)}`, { method: "DELETE", csrf });
